import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import LessonVideoPlayer from './LessonVideoPlayer';

function LessonProgressIcon({ lesson }) {
  if (lesson.completed) {
    return (
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1A87CC]">
        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  }

  if (lesson.watchProgressPercent > 0) {
    const radius = 8;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - lesson.watchProgressPercent / 100);

    return (
      <span className="relative flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden>
        <svg className="h-5 w-5 -rotate-90" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r={radius} fill="none" stroke="#D9EEF8" strokeWidth="2.5" />
          <circle
            cx="10"
            cy="10"
            r={radius}
            fill="none"
            stroke="#1A87CC"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
      </span>
    );
  }

  return null;
}

function PlaylistLessonItem({ lesson, isActive, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition ${
        isActive ? 'border-l-4 border-[#1A87CC] bg-[#E8F4FC]' : 'hover:bg-[#F8FBFF]'
      }`}
    >
      <svg className="h-4 w-4 shrink-0 text-[#1A87CC]" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path d="M8 5v14l11-7z" />
      </svg>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${isActive ? 'text-[#1A87CC]' : 'text-[#1B1B1B]'}`}>
          {lesson.title}
        </p>
        <p className="mt-0.5 text-xs text-[#666666]">{lesson.duration}</p>
      </div>
      {lesson.isDownloaded ? (
        <span className="shrink-0 rounded-full bg-[#F3F0FF] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#9369CC]">
          Offline
        </span>
      ) : null}
      <LessonProgressIcon lesson={lesson} />
    </button>
  );
}

function PlaylistPlayerSection({
  playlist,
  activeLesson,
  activeLessonId,
  onLessonSelect,
  onLessonProgress,
  onLessonComplete,
  onVideoError,
}) {
  const navigate = useNavigate();
  const { isLoggedIn, openAuthPopup } = useAuthContext();

  if (!playlist || !activeLesson) {
    return (
      <div className="rounded-2xl bg-white p-8 text-center text-sm text-[#666666] shadow-sm">
        No lessons available for this chapter.
      </div>
    );
  }

  const currentLessonId = activeLessonId ?? activeLesson.id;

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
      <div className="overflow-hidden rounded-2xl bg-black shadow-lg">
        <LessonVideoPlayer
          lesson={activeLesson}
          isLoggedIn={isLoggedIn}
          onRequireAuth={openAuthPopup}
          onRequireSubscription={() => navigate('/price')}
          onProgressSync={onLessonProgress}
          onComplete={onLessonComplete}
          onError={onVideoError}
        />
      </div>

      <aside className="rounded-2xl border border-[#E8E8E8] bg-white p-4 shadow-[0_4px_20px_rgba(0,0,0,0.06)]">
        <h2 className="text-base font-bold text-[#1B1B1B]">{playlist.title}</h2>
        <p className="mt-1 text-xs font-medium text-[#666666]">
          {playlist.lectureCount} Lectures | {playlist.duration}
        </p>

        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#E9E9E9]">
          <div
            className="h-full rounded-full bg-[#69991D]"
            style={{ width: `${playlist.progressPercent}%` }}
          />
        </div>

        <div className="mt-4 max-h-[340px] space-y-1 overflow-y-auto pr-1">
          {playlist.lessons.map((lesson) => (
            <PlaylistLessonItem
              key={lesson.id}
              lesson={lesson}
              isActive={Number(lesson.id) === Number(currentLessonId)}
              onSelect={() => onLessonSelect?.(lesson.id)}
            />
          ))}
        </div>
      </aside>
    </div>
  );
}

export default PlaylistPlayerSection;
